import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { RootState, AppDispatch } from "../../../redux/store";
import { createDeployment } from "../../../redux/actions/deploymentActions";
import {
  fetchBuild,
  fetchAllBuilds,
} from "../../../redux/actions/buildActions";
import { fetchAllBehaviorConfigs } from "../../../redux/actions/behaviorConfigActions";

const CreateDeploymentForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("organization_id");

  const { loading: deploymentLoading, error: deploymentError } = useSelector(
    (state: RootState) => state.deployments
  );

  const {
    builds,
    currentBuild,
    loading: buildLoading,
    error: buildError,
  } = useSelector((state: RootState) => state.builds);

  const {
    behaviorConfigs,
    loading: behaviorConfigLoading,
    error: behaviorConfigError,
  } = useSelector((state: RootState) => state.behaviorConfigs);

  // Fetch all builds and behavior configs when the component mounts
  useEffect(() => {
    dispatch(fetchAllBuilds());
    dispatch(fetchAllBehaviorConfigs());
  }, [dispatch]);

  const [buildId, setBuildId] = useState("");
  const [behaviorConfigId, setBehaviorConfigId] = useState("");
  const [minHosts, setMinHosts] = useState<number | undefined>(0);
  const [maxHosts, setMaxHosts] = useState<number | undefined>(1);
  const [minHostsIsNone, setMinHostsIsNone] = useState(false);
  const [maxHostsIsNone, setMaxHostsIsNone] = useState(false);
  const [buildIdError, setBuildIdError] = useState("");
  const [behaviorConfigIdError, setBehaviorConfigIdError] = useState("");
  const [hostValidationError, setHostValidationError] = useState("");
  const [isTestbedDeployment, setIsTestbedDeployment] = useState(false);
  const [disablePhantomSessions, setDisablePhantomSessions] = useState(true);

  // Validate build ID
  useEffect(() => {
    if (buildId) {
      dispatch(fetchBuild(buildId));
    }
  }, [dispatch, buildId]);

  const validateHosts = useCallback(() => {
    if (
      !minHostsIsNone &&
      !maxHostsIsNone &&
      minHosts !== undefined &&
      maxHosts !== undefined &&
      minHosts > maxHosts
    ) {
      setHostValidationError(
        "Min hosts must be less than or equal to max hosts"
      );
      return false;
    }
    setHostValidationError("");
    return true;
  }, [minHosts, maxHosts, minHostsIsNone, maxHostsIsNone]);

  useEffect(() => {
    validateHosts();
  }, [validateHosts]);

  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setBuildIdError("");
    setBehaviorConfigIdError("");

    // Validate build ID
    if (!buildId) {
      setBuildIdError("Build ID is required");
      isValid = false;
    }

    // Validate behavior config ID
    if (!behaviorConfigId) {
      setBehaviorConfigIdError("Behavior Config is required");
      isValid = false;
    }

    // Validate hosts
    if (!validateHosts()) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationId) {
      toast.error("Organization ID is required");
      return;
    }

    if (!validateForm()) {
      return;
    }

    const deploymentData = {
      organization_id: organizationId,
      build_id: buildId,
      behavior_config_id: behaviorConfigId,
      min_hosts: minHostsIsNone ? null : minHosts || 0,
      max_hosts: maxHostsIsNone ? null : maxHosts || 1,
      is_testbed_deployment: isTestbedDeployment,
      disable_phantom_sessions: disablePhantomSessions,
    };

    dispatch(createDeployment(deploymentData))
      .unwrap()
      .then((deployment) => {
        toast.success("Deployment created successfully");
        navigate(
          `/dashboard/deployments/${deployment.id}?organization_id=${organizationId}`,
          {
            state: { toastMessage: "Deployment created successfully" },
          }
        );
      })
      .catch((error) => {
        toast.error(`Failed to create deployment: ${error.message}`);
      });
  };

  if (!organizationId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Organization ID is required to create a deployment
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/dashboard/organizations")}
          sx={{ mt: 2 }}
        >
          Back to Organizations
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h5" gutterBottom>
        Create New Deployment
      </Typography>
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Organization ID"
            value={organizationId}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth margin="normal" error={!!buildIdError}>
            <InputLabel id="build-select-label">Build</InputLabel>
            <Select
              labelId="build-select-label"
              value={buildId}
              onChange={(e) => setBuildId(e.target.value)}
              label="Build"
              required
            >
              {buildLoading ? (
                <MenuItem disabled>Loading builds...</MenuItem>
              ) : builds.length > 0 ? (
                builds.map((build) => (
                  <MenuItem key={build.id} value={build.id}>
                    {build.build_name} ({build.id})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No builds available</MenuItem>
              )}
            </Select>
            {buildIdError && <FormHelperText>{buildIdError}</FormHelperText>}
            {!buildIdError && currentBuild && (
              <FormHelperText>
                Selected: {currentBuild.build_name}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            margin="normal"
            error={!!behaviorConfigIdError}
          >
            <InputLabel id="behavior-config-select-label">
              Behavior Config
            </InputLabel>
            <Select
              labelId="behavior-config-select-label"
              value={behaviorConfigId}
              onChange={(e) => setBehaviorConfigId(e.target.value)}
              label="Behavior Config"
              required
            >
              {behaviorConfigLoading ? (
                <MenuItem disabled>Loading behavior configs...</MenuItem>
              ) : behaviorConfigs.length > 0 ? (
                behaviorConfigs.map((config) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.behavior_name} ({config.id})
                    {config.demo_mode ? ` - ${config.demo_mode}` : ""}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No behavior configs available</MenuItem>
              )}
            </Select>
            {behaviorConfigIdError && (
              <FormHelperText>{behaviorConfigIdError}</FormHelperText>
            )}
          </FormControl>

          <Box>
            <TextField
              label="Min Hosts"
              value={
                minHostsIsNone
                  ? ""
                  : minHosts !== undefined
                    ? minHosts.toString()
                    : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setMinHosts(undefined);
                } else if (/^\d+$/.test(value)) {
                  const numValue = parseInt(value, 10);
                  if (numValue >= 0) {
                    setMinHosts(numValue);
                  }
                }
              }}
              fullWidth
              margin="normal"
              disabled={minHostsIsNone}
              error={
                !!hostValidationError && !minHostsIsNone && !maxHostsIsNone
              }
              helperText={
                !minHostsIsNone && !maxHostsIsNone && hostValidationError
                  ? hostValidationError
                  : !minHostsIsNone
                    ? "Enter a number 0 or greater"
                    : ""
              }
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={minHostsIsNone}
                  onChange={(e) => {
                    setMinHostsIsNone(e.target.checked);
                    if (e.target.checked) {
                      setMinHosts(undefined);
                    } else {
                      setMinHosts(1);
                    }
                  }}
                />
              }
              label="Set to None"
            />
          </Box>

          <Box>
            <TextField
              label="Max Hosts"
              value={
                maxHostsIsNone
                  ? ""
                  : maxHosts !== undefined
                    ? maxHosts.toString()
                    : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setMaxHosts(undefined);
                } else if (/^\d+$/.test(value)) {
                  const numValue = parseInt(value, 10);
                  if (numValue >= 0) {
                    setMaxHosts(numValue);
                  }
                }
              }}
              fullWidth
              margin="normal"
              disabled={maxHostsIsNone}
              error={
                !!hostValidationError && !minHostsIsNone && !maxHostsIsNone
              }
              helperText={
                !minHostsIsNone && !maxHostsIsNone && hostValidationError
                  ? hostValidationError
                  : !maxHostsIsNone
                    ? "Enter a number 0 or greater"
                    : ""
              }
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={maxHostsIsNone}
                  onChange={(e) => {
                    setMaxHostsIsNone(e.target.checked);
                    if (e.target.checked) {
                      setMaxHosts(undefined);
                    } else {
                      setMaxHosts(1);
                    }
                  }}
                />
              }
              label="Set to None"
            />
          </Box>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={disablePhantomSessions}
                onChange={(e) => {
                  setDisablePhantomSessions(e.target.checked);
                }}
              />
            }
            label="Disable Phantom Sessions"
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={isTestbedDeployment}
                onChange={(e) => {
                  setIsTestbedDeployment(e.target.checked);
                }}
              />
            }
            label="Testbed Only Deployment"
          />
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                deploymentLoading ||
                buildLoading ||
                !!hostValidationError ||
                !buildId ||
                !behaviorConfigId
              }
            >
              {deploymentLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Create Deployment"
              )}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() =>
                navigate(`/dashboard/organizations/${organizationId}`)
              }
            >
              Back to Organization
            </Button>
          </Box>

          {(deploymentError || buildError || behaviorConfigError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deploymentError || buildError || behaviorConfigError}
            </Alert>
          )}
        </form>
      </Paper>
    </Box>
  );
};

export default CreateDeploymentForm;
